import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Check, Heart, Minus } from 'lucide-react'
import { pointerGlow } from '../../animations/gsapAnimations'
import type { Vehicle } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import { vehicleWhatsappHref } from '../../data/site'
import { useColourPick } from '../../hooks/useColourPick'
import { pkr, shortPrice } from '../../utils/format'
import { assurancesFor } from '../../utils/assurance'
import { purchaseOptionsFor, type PurchaseRoute } from '../../utils/purchase'
import ColourSwatches from './ColourSwatches'
import PurchaseRoutePicker from './PurchaseRoutePicker'
import BookingModal from '../booking/BookingModal'
import WhatsAppIcon from '../ui/WhatsAppIcon'
import VehicleMedia from './VehicleMedia'

/* The editorial vehicle card. Used by the new-arrivals rail and the
   similar-vehicles rail; the photograph and its silhouette fallback come from
   VehicleMedia, which the inventory grid's compact card shares.

   It is built as an editorial object: the photograph bleeds to the card's own
   top edge with a dark scrim rising from its foot, and the marque and year
   sit on the picture in white caps — the way a magazine captions a car, not
   the way a listing row labels one. Everything below reads against paper:
   the model name, the assurances, and the money settled at the foot beside
   one filled pill of a call to action.

   The swatches say which colour the car is wanted in — see useColourPick.
   Nothing on the card follows them: the money, the assurances and the
   photographs are this car's, and the pick is carried into the booking
   rather than re-quoting the card as another plate number. */


type VehicleCardProps = {
  vehicle: Vehicle
  /** The shortlist itself rather than one flag: what is saved is the car on
      the card, whichever colour has been picked on it. Omit it
      and onToggleSave to render a card with no shortlist control. */
  savedIds?: string[]
  onToggleSave?: (id: string) => void
  className?: string
}

function VehicleCard({
  vehicle,
  savedIds,
  onToggleSave,
  className = '',
}: VehicleCardProps) {
  const [bookingOpen, setBookingOpen] = useState(false)

  /* Which route the first payment below is quoted on. Card-local, like the
     grid card's: a visitor reading a rail is choosing per car. */
  const [route, setRoute] = useState<PurchaseRoute>('on-spot')

  /* The cursor spotlight (see `card-glow`): a pool of light tracks the
     pointer across the card. CSS draws it; this only feeds it coordinates. */
  const glowRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const el = glowRef.current
    if (!el) return
    return pointerGlow(el)
  }, [])

  /* The paints this one is offered in: its own colour and every other one the
     same trim and model year is standing on the floor in. The pick is local
     to the card and reaches nothing but the booking — see useColourPick. */
  const { colours, selected, selectedId, select } = useColourPick(vehicle)

  const assurances = assurancesFor(vehicle)

  /* The routes this one is offered on — both on most of the floor, one on a
     car the showroom will only book or only sell off the floor. A sold car
     drops the row: there is nothing left to order. */
  const routes = purchaseOptionsFor(vehicle)
  const choice = vehicle.status !== 'sold'
  const bookingAvailable = vehicle.status === 'in-stock' && vehicle.availability !== 'on-spot'
  const saved = savedIds?.includes(vehicle.id) ?? false

  const cover = vehicle.images[0] || ''

  return (
    <article
      ref={glowRef}
      className={`group card-glow relative flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-raised shadow-card transition duration-500 ease-lux hover:-translate-y-1.5 hover:border-lime/35 hover:shadow-pop ${className}`}
    >
      {/* ----- media -----
          Full bleed to the card's top edge — the photograph is the card's
          opening statement, so it wears no frame of its own. `overflow-hidden`
          on the article is what crops it to the corners; the modal is safe
          from that clip because it portals to the body. */}
      <div className="relative aspect-16/10 overflow-hidden">
        <VehicleMedia vehicle={vehicle} source={cover} />

        {/* The scrim. It earns the caption its contrast on any photograph,
            and it grounds the picture the way a vignette grounds an
            editorial spread — depth, not decoration. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-void/60 via-void/10 to-transparent"
        />

        {/* Marque and year ride the photograph as its caption, so the body
            below opens straight onto the model's name. White over the scrim
            — frosted glass here would be one material too many. */}
        <div className="absolute inset-x-4 bottom-3.5 z-1 flex items-center gap-2 label-sm text-white">
          {vehicle.brand}
          <span aria-hidden="true" className="size-1 rounded-full bg-white/40" />
          <span className="text-white/70 tabular-nums">{vehicle.year}</span>
        </div>

        {/* Only the state a car is *in* rides the top of the picture. */}
        {vehicle.tag ? (
          <span className="absolute top-3.5 left-3.5 z-1 rounded-full bg-void/45 px-3 py-1.5 ring-1 ring-void/12 backdrop-blur-md label-sm text-white">
            {vehicle.tag}
          </span>
        ) : null}

        {onToggleSave ? (
          <button
            type="button"
            aria-pressed={saved}
            aria-label={`${saved ? 'Remove' : 'Save'} ${nameOf(vehicle)}`}
            onClick={() => onToggleSave(vehicle.id)}
            className={`absolute top-3.5 right-3.5 z-2 grid size-9 place-items-center rounded-full backdrop-blur-md transition duration-200 ${
              saved
                ? 'bg-lime text-void shadow-action'
                : 'bg-void/35 text-white ring-1 ring-white/25 hover:bg-void/55'
            }`}
          >
            <Heart
              className="size-4.5"
              strokeWidth={1.8}
              fill={saved ? 'currentColor' : 'none'}
            />
          </button>
        ) : null}

        {/* The affordance that says the picture is a door. Hover-only,
            because on touch the whole card is already the target.
            Decorative — the stretched title link below is what actually
            navigates. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-3.5 bottom-3.5 z-1 grid size-9 translate-y-2 place-items-center rounded-full bg-lime text-void opacity-0 shadow-action backdrop-blur-md transition duration-300 ease-lux group-hover:translate-y-0 group-hover:opacity-100"
        >
          <ArrowUpRight strokeWidth={2} className="size-4.5" />
        </span>
      </div>

      {/* ----- copy ----- */}
      <div className="flex flex-1 flex-col px-5 pt-4.5 pb-5">
        {/* The title is the card's link, stretched over the whole surface —
            so the card is one click target and one accessible name, and the
            controls below simply sit above it. It opens this car, whichever
            colour has been picked on it. */}
        <h3 className="font-display text-[22px] leading-tight font-semibold tracking-[-0.02em] text-platinum">
          <Link
            to={`/cars/${vehicle.id}`}
            className="transition-colors duration-200 before:absolute before:inset-0 before:content-[''] hover:text-lime"
          >
            {vehicle.model}
            <span className="sr-only">
              {' '}
              {vehicle.brand} {vehicle.variant} {vehicle.year} — view details
            </span>
          </Link>
        </h3>
        {/* The make and the trim, under the model line that names the car.
            Three facts, split the way somebody says them out loud: "the
            Corolla — the Toyota Grande X". */}
        <p className="mt-1 text-[13.5px] leading-snug text-steel">
          {vehicle.brand}
          {vehicle.variant ? ` · ${vehicle.variant}` : ''}
        </p>

        {/* The paints this car is offered in, as swatches — the showroom's
            answer to "what colours does it come in", read off the floor.

            Pressing one says which paint this car is wanted in, and stops
            there: the money below does not move, the photograph does not
            change — a car is shot once for its trim — and the pick is only
            read again on the booking form.

            `relative z-2`, like the buttons at the foot of the card: the
            title link is stretched over the whole surface, so anything meant
            to be clickable in its own right has to sit above it or every
            click on it navigates to this car instead. */}
        <ColourSwatches
          colours={colours}
          value={selectedId}
          onChange={select}
          name={nameOf(vehicle)}
          size="md"
          className="relative z-2 mt-3"
        />

        {/* The buying assurances — the reason people pick a dealer, so they
            sit on the card rather than only on the detail page. Anything the
            car does not carry stays visible but muted; hiding it would read
            as a claim we have not made.

            Borderless pills rather than outlined ones: fill and ink do the
            sorting — included is a filled pill with a mint check, excluded
            a ghost. */}
        <div className="mt-4">
          <p className="label-sm text-dim">What the price carries</p>
          <ul className="mt-2.5 flex flex-wrap gap-1.5">
            {assurances.map((item) => (
              <li
                key={item.key}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  item.included
                    ? 'bg-surface text-silver'
                    : 'bg-surface/40 text-dim'
                }`}
              >
                {item.included ? (
                  <Check
                    aria-hidden="true"
                    strokeWidth={3}
                    className="size-3 shrink-0 text-mint-deep"
                  />
                ) : (
                  <Minus
                    aria-hidden="true"
                    strokeWidth={2.4}
                    className="size-3 shrink-0 text-dim"
                  />
                )}
                {item.short}
                <span className="sr-only">
                  {item.included ? ': included' : ': not included'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* `mt-auto` pins the money and the actions to the foot of the card,
            so a row of them lands its calls to action on one line however
            far the variant names wrap and however many paints each car is
            offered in.

            No tinted panel around the money any more — a hairline above it
            and the size of the figure are what say "this is the price". The
            biggest number on the card is the one the buyer decides on. It is
            this car's whatever the swatches are left on. */}
        <div className="mt-auto pt-4">
          <div className="flex items-end justify-between gap-3 border-t border-line-soft pt-4">
            {vehicle.financingAvailable && vehicle.instalment ? (
              <>
                <div className="min-w-0">
                  <span className="label-sm text-dim">Instalment from</span>
                  <strong className="mt-1.5 flex items-baseline gap-1 font-display text-[24px] leading-none font-semibold tracking-[-0.02em] text-platinum tabular-nums">
                    {pkr.format(vehicle.instalment)}
                    <span className="text-[12px] font-medium text-dim">/mo</span>
                  </strong>
                  {/* The deposit that monthly figure assumes, said on the
                      card rather than left to the detail page. A monthly
                      number with no deposit beside it is the one thing on a
                      listing a buyer cannot check, and this showroom quotes
                      a different one on some cars. */}
                  {vehicle.deposit !== null ? (
                    <span className="mt-1 block truncate text-[11.5px] text-dim tabular-nums">
                      after {shortPrice(vehicle.deposit)} down
                    </span>
                  ) : null}
                </div>
                <div className="shrink-0 text-right">
                  <span className="label-sm text-dim">Cash</span>
                  <strong
                    title={`PKR ${pkr.format(vehicle.price)}`}
                    className="mt-1.5 block text-[15px] leading-none font-semibold text-silver tabular-nums"
                  >
                    {shortPrice(vehicle.price)}
                  </strong>
                </div>
              </>
            ) : (
              /* Cash-only stock: the cash figure takes the big slot rather
                 than a "not available" apology — the card leads with what the
                 car *is*, and the greyed "Bank leased" pill above already
                 says what it is not. */
              <div className="min-w-0">
                <span className="label-sm text-dim">Cash price</span>
                <strong className="mt-1.5 flex items-baseline gap-1.5 font-display text-[24px] leading-none font-semibold tracking-[-0.02em] text-platinum tabular-nums">
                  <span className="text-[12px] font-medium text-dim">PKR</span>
                  {pkr.format(vehicle.price)}
                </strong>
              </div>
            )}
          </div>

          {/* How an order may be taken on this one: the pair to choose
              between, or — on a car the showroom offers a single way — the
              line saying which. Under the money, because the only figure it
              moves is the payment asked up front, and above the stretched
              title link so a press picks a route rather than opening the
              car. */}
          {choice ? (
            <PurchaseRoutePicker
              options={routes}
              value={route}
              onChange={setRoute}
              name={nameOf(vehicle)}
              className="relative z-2 mt-4"
            />
          ) : null}

          {/* The two ways this business takes an order: file it, or ask
              about it. They sit above the stretched title link — hence
              `relative` and the raised z — so a click on either is the
              action it says, not a navigation to the detail page.

              One filled pill and one round mark: the booking is the deal, so
              it takes the full width and the colour; WhatsApp is the aside,
              folded down to its own icon so it cannot compete with the order
              of the two. */}
          <div className={`relative z-2 mt-4 grid gap-2 ${bookingAvailable ? 'grid-cols-[1fr_auto]' : 'grid-cols-1'}`}>
            {bookingAvailable ? (
              <button
                type="button"
                onClick={() => setBookingOpen(true)}
                className="flex h-12 btn-sheen items-center justify-center gap-2 rounded-full bg-lime text-[13px] font-semibold tracking-[-0.01em] text-void shadow-action transition duration-300 hover:bg-lime-lift active:translate-y-px"
              >
                Book now
                <ArrowUpRight aria-hidden="true" strokeWidth={2} className="size-4 shrink-0" />
                <span className="sr-only">
                  {' '}
                  — {nameOf(vehicle)}
                </span>
              </button>
            ) : null}

            <a
              href={vehicleWhatsappHref(vehicle, selected?.name || vehicle.colour)}
              target="_blank"
              rel="noreferrer"
              aria-label={`WhatsApp — enquire about the ${nameOf(vehicle)}`}
              title="Enquire on WhatsApp"
              className={
                bookingAvailable
                  ? 'grid size-12 place-items-center rounded-full border border-mint/40 bg-mint/8 text-mint-deep transition duration-200 hover:border-mint hover:bg-mint/15 active:translate-y-px'
                  : 'flex h-12 items-center justify-center gap-2 rounded-full border border-mint/40 bg-mint/8 px-4 text-[13px] font-semibold text-mint-deep transition duration-200 hover:border-mint hover:bg-mint/15 active:translate-y-px'
              }
            >
              <WhatsAppIcon className="size-5 shrink-0" />
              {!bookingAvailable ? 'Enquire on WhatsApp' : null}
            </a>
          </div>
        </div>
      </div>

      {/* Mounted only while open, so a grid of twenty cars is not also a grid
          of twenty hidden dialogs. The booking is filed against this car, in
          the colour the swatches were left on. */}
      {bookingOpen && bookingAvailable ? (
        <BookingModal
          vehicle={vehicle}
          colour={selected}
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
        />
      ) : null}
    </article>
  )
}

export default VehicleCard
